import { type FC, useEffect, useState } from "react";
import type { WebAuth } from "auth0-js";
import {
  Form,
  FormRow,
  TextField,
  SubmitButton,
  Text,
  LinkButton,
  Flex,
  Spacing,
  Button,
  type FormProps,
  Grid,
  Tooltip,
  Icon,
  Message,
  PasswordField,
} from "@huub-web/ui";
import { useTranslation } from "react-i18next";

export type LoginFormProps = {
  webAuth: WebAuth | null;
  error: string | null;
  onSubmit: (email: string, password: string) => Promise<void>;
  onSwitchToRegister: () => void;
  onGoogleLogin: () => Promise<void>;
  onMicrosoftLogin: () => Promise<void>;
  onLoadingChange: (loading: boolean) => void;
  onForgotPassword: () => void;
};

/**
 * LoginForm component for user authentication.
 * Displays email and password fields with validation.
 */
export const LoginForm: FC<LoginFormProps> = ({
  webAuth,
  error,
  onSubmit,
  onSwitchToRegister,
  onGoogleLogin,
  onMicrosoftLogin,
  onLoadingChange,
  onForgotPassword,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const handleSubmit: FormProps["onSubmit"] = (_e, valid) => {
    if (!valid || !webAuth) {
      return;
    }

    const submitAsync = async () => {
      setLoading(true);

      try {
        await onSubmit(email, password);
      } finally {
        setLoading(false);
      }
    };

    void submitAsync();
  };

  return (
    <>
      <Grid variant="twoColumnFixed">
        <Tooltip value={t("registration.googleTooltip")}>
          <Button
            variant="opaque"
            border="input"
            onClick={() => void onGoogleLogin()}
            disabled={loading}
            size="l"
            align="center"
          >
            <Icon
              icon={
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM8 12C8 9.79086 9.79086 8 12 8C13.1048 8 14.1035 8.44662 14.8284 9.17157C15.219 9.5621 15.8521 9.5621 16.2426 9.17157C16.6332 8.78105 16.6332 8.14788 16.2426 7.75736C15.1579 6.67267 13.6566 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12C18 11.4477 17.5523 11 17 11H12C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13H15.874C15.4299 14.7252 13.8638 16 12 16C9.79086 16 8 14.2091 8 12Z"
                  fill="currentColor"
                />
              }
            />
          </Button>
        </Tooltip>
        <Tooltip value={t("registration.microsoftTooltip")}>
          <Button
            variant="opaque"
            border="input"
            onClick={() => void onMicrosoftLogin()}
            disabled={loading}
            size="l"
            align="center"
          >
            <Icon
              icon={
                <>
                  <path
                    d="M12.9697 12.9697C13.1103 12.829 13.3011 12.75 13.5 12.75H22C22.4142 12.75 22.75 13.0858 22.75 13.5V22C22.75 22.4142 22.4142 22.75 22 22.75H13.5C13.0858 22.75 12.75 22.4142 12.75 22V13.5C12.75 13.3011 12.829 13.1103 12.9697 12.9697Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1.46967 1.46967C1.61032 1.32902 1.80109 1.25 2 1.25H10.5C10.9142 1.25 11.25 1.58579 11.25 2V10.5C11.25 10.9142 10.9142 11.25 10.5 11.25H2C1.58579 11.25 1.25 10.9142 1.25 10.5V2C1.25 1.80109 1.32902 1.61032 1.46967 1.46967Z"
                    fill="currentColor"
                  />
                  <path
                    d="M1.46967 12.9697C1.61032 12.829 1.80109 12.75 2 12.75H10.5C10.9142 12.75 11.25 13.0858 11.25 13.5V22C11.25 22.4142 10.9142 22.75 10.5 22.75H2C1.58579 22.75 1.25 22.4142 1.25 22V13.5C1.25 13.3011 1.32902 13.1103 1.46967 12.9697Z"
                    fill="currentColor"
                  />
                  <path
                    d="M12.7491 2C12.7491 1.58579 13.0849 1.25 13.4991 1.25H21.9991C22.4133 1.25 22.749 1.58574 22.7491 1.99992L22.75 10.4999C22.75 10.6988 22.671 10.8896 22.5304 11.0303C22.3897 11.171 22.1989 11.25 22 11.25H13.4991C13.0849 11.25 12.7491 10.9142 12.7491 10.5V2Z"
                    fill="currentColor"
                  />
                </>
              }
            />
          </Button>
        </Tooltip>
      </Grid>

      <Text align="center">{t("authScreens.login.or")}</Text>

      <Form disabled={loading} onSubmit={handleSubmit}>
        <FormRow id="email">
          <TextField
            name="email"
            label={t("registration.emailLabel")}
            value={email}
            onChange={setEmail}
            validation={["required", "email"]}
            ignorePasswordManagers={false}
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
          />
        </FormRow>

        <FormRow id="password">
          <PasswordField
            label={t("registration.passwordLabel")}
            name="password"
            value={password}
            onChange={setPassword}
            validation={["required"]}
          />
        </FormRow>

        {error && <Message variant="error" body={error} />}

        <SubmitButton variant="primary" size="s" align="center">
          {t("authScreens.login.submitButton")}
        </SubmitButton>
      </Form>

      <Flex direction="column" align="center" gap="xs">
        <LinkButton type="button" onClick={onForgotPassword} disabled={loading}>
          {t("authScreens.login.forgotPassword")}
        </LinkButton>
        <LinkButton
          type="button"
          onClick={onSwitchToRegister}
          disabled={loading}
        >
          {t("authScreens.login.switchToRegister")}
        </LinkButton>
        <Spacing height="s" />
      </Flex>
    </>
  );
};
